import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import { classifyMemberError, MemberErrorKind } from './memberErrors';
import { memberDisplayName } from './memberLabels';
import { ProjectMember } from './types';

const useStyles = makeStyles(theme => ({
  field: {
    marginTop: theme.spacing(2),
  },
  caution: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(2),
  },
}));

type TransferOwnershipDialogProps = {
  open: boolean;
  candidates: ProjectMember[];
  onConfirm: (member: ProjectMember) => Promise<void>;
  onClose: () => void;
};

export function TransferOwnershipDialog({
  open,
  candidates,
  onConfirm,
  onClose,
}: TransferOwnershipDialogProps) {
  const classes = useStyles();
  const { t } = useTranslation('projects');
  const [selectedId, setSelectedId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorKind, setErrorKind] = useState<MemberErrorKind>();

  useEffect(() => {
    if (open) return;
    setSelectedId('');
    setErrorKind(undefined);
  }, [open]);

  const selected = candidates.find(member => member.id === selectedId);

  const submit = async () => {
    if (!selected) return;
    setSubmitting(true);
    setErrorKind(undefined);
    try {
      await onConfirm(selected);
      onClose();
    } catch (err) {
      setErrorKind(classifyMemberError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      onClose={() => {
        if (!submitting) onClose();
      }}
    >
      <DialogTitle>{t('members.transferTitle')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          {t('members.transferDescription')}
        </Typography>
        <TextField
          select
          fullWidth
          className={classes.field}
          label={t('members.transferSelectLabel')}
          value={selectedId}
          onChange={event => setSelectedId(event.target.value)}
          variant="outlined"
          SelectProps={{
            SelectDisplayProps: {
              'aria-label': t('members.transferSelectLabel'),
            },
          }}
        >
          {candidates.map(member => (
            <MenuItem key={member.id} value={member.id}>
              {[memberDisplayName(member, t('members.selfName')), member.email]
                .filter(Boolean)
                .join(' · ')}
            </MenuItem>
          ))}
        </TextField>
        {errorKind && (
          <Typography variant="body2" className={classes.caution}>
            {t(`members.errors.${errorKind}`)}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={submitting} onClick={onClose}>
          {t('actions.cancel')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={!selected || submitting}
          onClick={submit}
        >
          {submitting
            ? t('members.transferring')
            : t('members.transferConfirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
